import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import styled, { css, useTheme } from 'styled-components';
import { ColorSchemeSwitcher, NavArrow } from '@monavate/ui-core';
import { NavLink } from 'react-router-dom';
import { RootState, useAppSelector } from '../../reduxStore/rootReducer';
import { clientViewService } from '@/helpers/clientViewService';

const DropdownList = styled.div`
    display: none;
    position: absolute;
    top: 38px;
    right: 0;
    background: transparent;
    z-index: 9;
`;

const DropdownContent = styled.div`
    margin-top: 7px;
    overflow: hidden;
    border-radius: 0;
    border: 1px solid ${(props) => props.theme['Borders/quiet']};
    background: ${(props) => props.theme['Surface/page']};
`;

const Wrapper = styled.div`
    position: relative;
    color: ${(props) => props.theme['Content/primary']};
    background: ${(props) => props.theme['Surface/primary']};
    font-family: 'Poppins', serif;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    height: 24px;
    cursor: pointer;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    text-decoration: none;
    border: 1px solid ${(props) => props.theme['Borders/quiet']};

    &:hover {
        background: ${(props) => props.theme['Surface/secondary']};
        border-color: ${(props) => props.theme['Borders/hover']};
        ${DropdownList} {
            display: block;
        }
    }

    &.active {
        background: ${(props) => props.theme['Surface/secondary']};
    }
`;

const ThemeSwitcherDropdownItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    line-height: 22px;
    height: 32px;
    padding: 5px 24px 5px 16px;
`;

const SwitcherContainer = styled.div`
    width: 132px;
    height: 32px;
`;

const UserInfo = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
`;

const Separator = styled.div`
    background: ${(props) => props.theme['Borders/quiet']};
    height: 1px;
    margin: 0 16px 4px;
`;

const UserIconContainer = styled.div`
    margin-top: 6px;
`;

const UserInfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
`;

// Shared by the Account Settings link and the Log Out action, so both look the same.
const menuItemStyles = css`
    font-weight: 400;
    line-height: 22px;
    text-decoration: none;
    color: ${(props) => props.theme['Content/primary']};
    font-family: 'Poppins', serif;
    display: flex;
    height: 32px;
    padding: 5px 16px;
    align-items: center;
    gap: 12px;
    align-self: stretch;
    cursor: pointer;
    min-width: 295px;
    &:hover {
        background: ${(props) => props.theme['Surface/secondary']};
        color: ${(props) => props.theme['Content/secondary']};
    }
    &.active {
        background: ${(props) => props.theme['Surface/secondary']};
        font-weight: 500;
    }
`;

// A real link (not onClick + navigate) so it can be opened in a new tab.
const MenuItemNavLink = styled(NavLink)`
    ${menuItemStyles}
`;

const MenuItemLink = styled.div<{ id?: string }>`
    ${menuItemStyles}

    ${(props) =>
        props.id === 'log-out' &&
        `
            color: ${props.theme['Status/danger/content']}
    `}
`;

const MenuItemText = styled.div<{ id?: string }>`
    text-decoration: none;
    display: block;
    padding: 5px 16px;
    cursor: default;
    border-radius: 8px;
    min-width: 295px;
    max-width: 500px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${(props) => props.theme['Content/primary']};
    font-family: 'Poppins', serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
`;

const MenuItemTitle = styled(MenuItemText)`
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 22px;
    color: ${(props) => props.theme['Content/primary']};
    font-family: 'Poppins', serif;
`;

const processString = (input?: string): string => {
    if (input) {
        const [firstWord, ...restWords] = input.split(' ');
        if (restWords.length > 0) {
            return `${firstWord} ${restWords[0][0]}.`;
        } else {
            return input;
        }
    }
    return 'Profile';
};

export const UserDropdown: React.FC = () => {
    const { user, logout } = useAuth0();
    const [openDropDown, setOpenDropDown] = useState(false);
    const clientConfigurations = useAppSelector((state: RootState) => state.commonTerms.config);
    const onLogoutClickHandler = () => {
        clientViewService.clearClientViewData();
        logout({ logoutParams: { returnTo: window.location.origin } });
    };

    // @ts-ignore - theme extended with runtime props in index.tsx
    const { preference, setPreference } = useTheme();

    const onColorSwitcherChangeHandler = (value: string) => {
        setPreference(value);
    };

    return (
        <Wrapper
            onMouseEnter={() => setOpenDropDown(true)}
            onMouseLeave={() => setOpenDropDown(false)}
        >
            {user && (
                <UserInfo>
                    <UserIconContainer>
                        <span className="material-symbols-sharp icon">person</span>
                    </UserIconContainer>
                </UserInfo>
            )}
            <DropdownList>
                <DropdownContent>
                    <MenuItemTitle data-test-id="value-user-name" title={user?.name}>
                        {user?.name}
                    </MenuItemTitle>
                    {clientConfigurations?.name ? (
                        <MenuItemText data-test-id="value-client-name">
                            {clientConfigurations?.name}
                        </MenuItemText>
                    ) : null}

                    <Separator />
                    <ThemeSwitcherDropdownItem>
                        <span>Theme</span>
                        <ColorSchemeSwitcher
                            value={preference}
                            onChange={onColorSwitcherChangeHandler}
                            dataTestId="switcher-color-scheme"
                        />
                    </ThemeSwitcherDropdownItem>
                    <Separator />
                    <MenuItemNavLink
                        to="/account-settings"
                        data-test-id="menu-item-account-settings"
                    >
                        Account Settings
                    </MenuItemNavLink>
                    <MenuItemLink
                        id={'log-out'}
                        onClick={onLogoutClickHandler}
                        data-test-id="menu-item-log-out"
                    >
                        Log Out
                    </MenuItemLink>
                </DropdownContent>
            </DropdownList>
        </Wrapper>
    );
};
